/**
 * /admin/videos/placements — which video each kind of coach sees at each spot.
 *
 * A row left empty falls back: a tier to "Paid, any plan", every audience to
 * "Everyone". Where nothing is set up the line, the coach page shows no video
 * at that spot, which is how a page ships before its video. A public
 * placement is seen signed out, so it has only the Everyone row.
 *
 * Aliases: video placements admin, intro video settings, video per plan
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";

import { VideoPicker } from "~/components/admin/video-picker";
import { cn } from "~/lib/cn";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  learningAdminKeys,
  placementsQueryOptions,
  setPlacementVideoFn,
  videosQueryOptions,
  type LibraryVideo,
} from "~/lib/functions/admin-learning";
import type { SetPlacementVideoInput } from "~/lib/shared/learning/admin-inputs";
import {
  inheritedAudience,
  isPublicVideoPlacementKey,
  placementAudiences,
  VIDEO_AUDIENCE_LABELS,
  VIDEO_PLACEMENT_KEYS,
  VIDEO_PLACEMENTS,
  type VideoAudience,
  type VideoPlacementDefinition,
  type VideoPlacementKey,
} from "~/lib/shared/learning/placements";

export const Route = createFileRoute("/admin/videos/placements")({
  staticData: { title: "Placements" },
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "placements:read"),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(placementsQueryOptions()),
      context.queryClient.ensureQueryData(videosQueryOptions()),
    ]);
  },
  component: PlacementsPage,
});

const TIER_AUDIENCES: ReadonlySet<VideoAudience> = new Set([
  "core",
  "pro",
  "pro_plus",
]);

function PlacementsPage() {
  const { data: assignments = {} } = useQuery(placementsQueryOptions());
  const { data: videos = [] } = useQuery(videosQueryOptions());
  const queryClient = useQueryClient();

  const assign = useMutation({
    mutationFn: (input: SetPlacementVideoInput) =>
      setPlacementVideoFn({ data: input }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: learningAdminKeys.all }),
    onError: () => toast.error("Couldn't save that video choice."),
  });

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-medium text-gray-900">Placements</h1>
        <p className="text-text-secondary max-w-2xl text-sm text-pretty">
          Choose the video each kind of coach sees. An empty row uses the row it
          falls back to; with nothing set, the page shows no video there. Add
          videos in the{" "}
          <Link
            className="text-purple-700 underline-offset-2 hover:underline"
            to="/admin/videos"
          >
            video library
          </Link>
          .
        </p>
      </div>

      {VIDEO_PLACEMENT_KEYS.map((placement) => (
        <PlacementCard
          assigned={assignments[placement] ?? {}}
          key={placement}
          onAssign={(audience, videoId) =>
            assign.mutate({ placement, audience, videoId })
          }
          placement={placement}
          videos={videos}
        />
      ))}
    </div>
  );
}

function PlacementCard({
  placement,
  assigned,
  videos,
  onAssign,
}: {
  placement: VideoPlacementKey;
  assigned: Partial<Record<VideoAudience, string>>;
  videos: readonly LibraryVideo[];
  onAssign: (audience: VideoAudience, videoId: string | null) => void;
}) {
  const definition: VideoPlacementDefinition = VIDEO_PLACEMENTS[placement];
  return (
    <section className="rounded-xl border border-gray-200 bg-white">
      <header className="border-b border-gray-100 px-4 py-3">
        <h2 className="font-medium text-gray-900">{definition.label}</h2>
        <p className="text-text-tertiary text-xs">{definition.where}</p>
      </header>
      <ul className="divide-y divide-gray-100">
        {placementAudiences(placement).map((audience) => {
          const videoId = assigned[audience];
          const inherited =
            videoId === undefined
              ? inheritedAudience(assigned, audience)
              : null;
          const inheritedTitle = inherited
            ? videos.find((video) => video.id === assigned[inherited])?.title
            : undefined;
          const emptyText = inherited
            ? `Uses ${VIDEO_AUDIENCE_LABELS[inherited]}${inheritedTitle ? `: ${inheritedTitle}` : ""}`
            : isPublicVideoPlacementKey(placement)
              ? "No video: visitors see no video here"
              : definition.help
                ? "No video: the page shows no How this works button, and Help leaves it out"
                : audience === "everyone"
                  ? "No default: coaches without their own row see no video"
                  : "No video";

          return (
            <li
              className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5"
              key={audience}
            >
              <span
                className={cn(
                  "w-32 flex-none text-sm text-gray-900",
                  TIER_AUDIENCES.has(audience) && "text-text-secondary pl-4",
                )}
              >
                {VIDEO_AUDIENCE_LABELS[audience]}
              </span>
              <div className="w-full max-w-xl min-w-0 flex-1 basis-72">
                <VideoPicker
                  context={{ kind: "placements" }}
                  dashed
                  label={`${definition.label} video for ${VIDEO_AUDIENCE_LABELS[audience]}`}
                  noneLabel="No video of its own"
                  onChange={(id) => onAssign(audience, id)}
                  placeholder={emptyText}
                  value={videoId ?? null}
                  videos={videos}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
